// Copyright 2017 Sorama B.V.

import { Disposable, TaskQueue } from "aurelia-framework";
import { Margin, Size, Pair, Rect } from "controls/plot/IPlotPart";
import { PlotParentBase } from "controls/plot/PlotParentBase";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { StringExtensions } from "extensions/StringExtensions";
import { WebGLExtensions } from "extensions/WebGLExtensions";
import { IExportable } from "libraries/Export/IExportable";
import { TextureProgram } from "./programs/TextureProgram";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { SVGNode } from "libraries/SVG/SVGNode";
import { IColorMap } from "services/ColorMap/IColorMap";
import { Generic } from "units/Generic";
import { Time } from "units/Time";
import { Unit, UnitValue, WeightedUnit } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";
import { WeightedSpectrum } from "units/WeightedSpectrum";
import { ZWeighting } from "units/Weighting";
import { IPlotParent } from "../IPlotParent";
import { MinMaxProgram } from "./programs/MinMaxProgram";
import { SpectrogramProgram } from "./programs/SpectrogramProgram";

export abstract class SpectrogramBasePlot extends PlotParentBase implements IExportable {
    private readonly taskQueue: TaskQueue;
    private readonly resizeListener: ResizeListener;

    //#region Constants
    private static ColorMapResolution = 256;
    private static MaxTextureValue = 16777215; //  0x00FFFFFF (Dont go any higher, shader floating point will start making large errors)
    // A 120Khz iV64 samplerate spectrum returns with 2401 frequency bins. We use 4096 since we don't downsample, and since GPU's like powers of two.
    public static MaximumTextureDimension = 4096;
    //#endregion

    //#region WebGL fields

    private gl: WebGLRenderingContext;

    private vertexBuffer: WebGLBuffer;
    private texelBuffer: WebGLBuffer;

    private vertexData: Float32Array;
    private texelData: Float32Array;

    private dataTexture: WebGLTexture;
    private copyTexture: WebGLTexture;
    private colorMapTexture: WebGLTexture;

    private spectrumTextureDataBuffer: ArrayBuffer;
    private spectrumTextureDataBufferUint8View: Uint8Array;
    private spectrumTextureDataBufferUint32View: Uint32Array;

    private colormapTextureDataBuffer: Uint8Array;

    private frameBuffer: WebGLFramebuffer;

    //#endregion

    //#region Shader Programs

    private spectrogramProgram: SpectrogramProgram;
    private minMaxProgram: MinMaxProgram;
    private textureProgram: TextureProgram;

    //#endregion

    //#region Bindables

    protected xAxisBoundStart: number;
    protected xAxisBoundEnd: number;
    protected yAxisBoundStart: number;
    protected yAxisBoundEnd: number;

    protected abstract fixedMinimum: UnitValue;
    protected abstract fixedMaximum: UnitValue;
    protected abstract dataMinimum: UnitValue;
    protected abstract dataMaximum: UnitValue;

    protected abstract colormap: IColorMap;
    protected abstract displayUnit: Unit;

    public yUnit = Generic.unitless;

    //#endregion

    //#region Fields

    protected spectrumBacklog: UnitSpectrum[];

    private subscriptions: Disposable[];
    private vertexRanges: { start: number; length: number }[];
    private textureIndex = 0;
    private textureCoordinateIndex = 0;

    private max = 200;
    private min = -200;

    private currentExtrema: { min: number; max: number };

    private isAttached: boolean;
    private isFullScreen: boolean;

    //#endregion

    //#region Constructor and Initialisation

    constructor(resizeListener: ResizeListener, taskQueue: TaskQueue) {
        super();
        this.taskQueue = taskQueue;
        this.resizeListener = resizeListener;
        this.subscriptions = [];
    }

    public bind(context: IPlotParent): void {
        super.bind(context);

        this.subscriptions.push(
            this.resizeListener.elementResizeEvent(this.controlContainer, () => this.requestMeasure(true))
        );
        document.addEventListener("fullscreenchange", this.fullscreenChanged);

        this.initWebGl();
        this.initShaders();
        this.initBuffers();
        this.initTextures();
        this.colormapChanged();
    }

    public attached(): void {
        this.isAttached = true;

        // Wait for auralia to finish layouting, then measure the control
        this.taskQueue.queueTask(() => {
            this.requestMeasure(true);
        });
    }

    public detached(): void {
        this.isAttached = false;
    }

    public unbind(): void {
        document.removeEventListener("fullscreenchange", this.fullscreenChanged);

        while (this.subscriptions.length > 0) this.subscriptions.pop().dispose();
    }

    //#endregion

    //#region IPlotParent

    public requestMeasure(force = false): void {
        if (!this.isAttached) return;

        // Measure the control
        const redraw = this.measureControl(force);
        if (!force && !redraw) {
            return; // No change in measure, skip the update
        }

        // Since we always enforce a square aspect ratio (since SVG linewidth = 1px is not supported everywhere) we need to measure the viewport as well
        this.measureViewPort();

        // After the layouting has updated, force a redraw
        this.taskQueue.queueTask(() => this.redraw());
    }

    public requestViewPort(): void {
        this.measureViewPort();
    }

    public roundToNearestBin(value: number, axis: string): number {
        if (axis !== "y") {
            return value;
        }
        return value;
    }

    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        const svgNode = new SVGNode("svg");
        svgNode.setAttribute(
            "viewBox",
            StringExtensions.format(
                "{0},{1},{2},{3}",
                (-this.margin.left).toFixed(0),
                (-this.margin.top - 10).toFixed(0),
                (this.size.width + 10).toFixed(0),
                (this.size.height + 10).toFixed(0)
            )
        );
        svgNode.setAttribute("shape-rendering", "geometricPrecision");

        // Prevents NaN and ensures that the values are always equal or greater than zero
        const svgSize = {
            width: Math.max(0, (this.size?.width ?? 0) - (this.margin?.left ?? 0) - (this.margin?.right ?? 0)),
            height: Math.max(0, (this.size?.height ?? 0) - (this.margin?.top ?? 0) - (this.margin?.bottom ?? 0)),
        };

        const time = new Date().getTime();
        const clipPathId = "svgclip" + time;

        // Generate a defs section with a clip path
        const defsNode = new SVGNode("defs");
        const clipPathNode = new SVGNode("clipPath");
        clipPathNode.setAttribute("id", clipPathId);

        const rectNode = new SVGNode("rect");
        rectNode.setAttribute("x", "0");
        rectNode.setAttribute("y", "0");
        rectNode.setAttribute("width", svgSize.width.toFixed(0));
        rectNode.setAttribute("height", svgSize.height.toFixed(0));

        clipPathNode.addChild(rectNode);
        defsNode.addChild(clipPathNode);
        svgNode.addChild(defsNode);

        // Add the waterfall image nodes
        this.redraw();
        const spectrogram = (<HTMLCanvasElement>this.plotElement).toDataURL("image/png");
        const gNode = new SVGNode("g");
        gNode.setAttribute("clip-path", "url(#" + clipPathId + ")");

        const imageNode = new SVGNode("image");
        imageNode.setAttribute("x", "0");
        imageNode.setAttribute("y", "0");
        imageNode.setAttribute("width", svgSize.width.toFixed(0));
        imageNode.setAttribute("height", svgSize.height.toFixed(0));
        imageNode.setAttribute("xlink:href", spectrogram);

        gNode.addChild(imageNode);
        svgNode.addChild(gNode);

        // For each plot part, check if they implement exportToSvg
        const partPromises = this.plotParts.map((part) => {
            const exportablePart = <IExportable>(<unknown>part);
            if (!exportablePart.exportToSvg) return Promise.resolve(undefined);

            return exportablePart.exportToSvg().then((partSvgNode) => {
                if (partSvgNode) {
                    // Remove SVG parent and replace with g
                    const gNode = new SVGNode("g");
                    for (const child of partSvgNode.children) {
                        gNode.addChild(child);
                    }

                    return gNode;
                }
            });
        });

        return Promise.all(partPromises).then((nodes) => {
            for (const node of nodes.filter((x) => x)) {
                svgNode.addChild(node);
            }

            return svgNode;
        });
    }

    //#endregion

    //#region Initialise Functions

    private initWebGl(): void {
        const canvas = <HTMLCanvasElement>this.plotElement;
        this.gl = <WebGLRenderingContext>canvas.getContext("webgl", { preserveDrawingBuffer: true });
        if (!this.gl) {
            this.gl = <WebGLRenderingContext>canvas.getContext("experimental-webgl", { preserveDrawingBuffer: true });
        }
        if (!this.gl) {
            throw new Error("WebGL is not supported by this browser");
        }

        if (!this.colormap) {
            this.gl.clearColor(0, 0, 0, 1);
        } else {
            const clearColor = this.colormap.toColor(0);
            this.gl.clearColor(clearColor.r, clearColor.g, clearColor.b, clearColor.a);
        }

        WebGLExtensions.ThrowOnError(this.gl);
    }

    private initBuffers(): void {
        // Create buffers
        this.vertexBuffer = this.gl.createBuffer();
        this.texelBuffer = this.gl.createBuffer();

        // We use 32 bit greyscale values. But we pass them to the shader as 4 8bit values (RGBA).
        // So writing will use the Uint32 view, and reading / sending to the shader is what the Uint8View is for.
        this.spectrumTextureDataBuffer = new ArrayBuffer(
            SpectrogramBasePlot.MaximumTextureDimension * SpectrogramBasePlot.MaximumTextureDimension * 4
        );
        this.spectrumTextureDataBufferUint8View = new Uint8Array(this.spectrumTextureDataBuffer);
        this.spectrumTextureDataBufferUint32View = new Uint32Array(this.spectrumTextureDataBuffer);

        // Pre allocate arrays
        this.spectrumBacklog = <UnitSpectrum[]>new Array(SpectrogramBasePlot.MaximumTextureDimension);

        WebGLExtensions.ThrowOnError(this.gl);
    }

    private initTextures(): void {
        this.frameBuffer = this.gl.createFramebuffer();
        this.dataTexture = this.gl.createTexture();
        this.copyTexture = this.gl.createTexture();
        this.colorMapTexture = this.gl.createTexture();

        // Create a new texture of maximum size (this.maximumTextureValue * this.maximumTextureValue)
        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.dataTexture);
        this.gl.texImage2D(
            WebGLRenderingContext.TEXTURE_2D,
            0,
            this.gl.RGBA,
            SpectrogramBasePlot.MaximumTextureDimension,
            SpectrogramBasePlot.MaximumTextureDimension,
            0,
            this.gl.RGBA,
            this.gl.UNSIGNED_BYTE,
            null
        );

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

        // Create a second texture and framebuffer
        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.copyTexture);
        this.gl.texImage2D(
            WebGLRenderingContext.TEXTURE_2D,
            0,
            this.gl.RGBA,
            SpectrogramBasePlot.MaximumTextureDimension,
            SpectrogramBasePlot.MaximumTextureDimension,
            0,
            this.gl.RGBA,
            this.gl.UNSIGNED_BYTE,
            null
        );

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

        // Generate a framebuffer and bind texture
        this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, this.frameBuffer);
        this.gl.framebufferTexture2D(
            WebGLRenderingContext.FRAMEBUFFER,
            WebGLRenderingContext.COLOR_ATTACHMENT0,
            WebGLRenderingContext.TEXTURE_2D,
            this.copyTexture,
            0
        );

        // Unbind all
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, null);
        this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, null);

        const count = (SpectrogramBasePlot.MaximumTextureDimension + 1) * 4; // 4 vertices per spectrum(waterfall-bin), + 1 to wrap up the end
        this.vertexData = new Float32Array(count);
        this.texelData = new Float32Array(count);
        for (let i = 0; i < count; i++) {
            this.vertexData[i] = 0.0;
            this.texelData[i] = 0.0;
        }

        // Init buffers
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, this.vertexData, this.gl.DYNAMIC_DRAW);

        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texelBuffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, this.texelData, this.gl.DYNAMIC_DRAW);

        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);
        this.textureIndex = 0;

        WebGLExtensions.ThrowOnError(this.gl);

        this.updateColorMapTexture();
    }

    private initBlending(): void {
        this.gl.enable(this.gl.BLEND);
        this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
        this.gl.blendEquation(this.gl.FUNC_ADD);

        WebGLExtensions.ThrowOnError(this.gl);
    }

    private initShaders(): void {
        this.spectrogramProgram = new SpectrogramProgram(this.gl);
        this.minMaxProgram = new MinMaxProgram(this.gl, SpectrogramBasePlot.MaximumTextureDimension);
        this.textureProgram = new TextureProgram(this.gl, SpectrogramBasePlot.MaximumTextureDimension);
    }

    //#endregion

    //#region Change Handlers

    private measureControl(force: boolean): boolean {
        if (
            !this.controlContainer ||
            (!force &&
                this.controlContainer.clientWidth === this.size.width &&
                this.controlContainer.clientHeight === this.size.height) ||
            this.controlContainer.clientWidth === 0 ||
            this.controlContainer.clientHeight === 0
        )
            // Not initialised or no change, no need to measure
            return false;

        // Get the size from the containing div element
        this.size = new Size(this.controlContainer.clientWidth, this.controlContainer.clientHeight);

        // Measure children
        const margin = new Margin(0, 0, 0, 0);

        this.plotParts.forEach((x) => {
            const partMargin = x.onMeasureMargin();

            margin.left = Math.max(margin.left, partMargin.left);
            margin.top = Math.max(margin.top, partMargin.top);
            margin.right = Math.max(margin.right, partMargin.right);
            margin.bottom = Math.max(margin.bottom, partMargin.bottom);
        });

        if (margin.left + margin.right > this.size.width) {
            margin.left = 0;
            margin.right = 0;
        }

        if (margin.top + margin.bottom > this.size.height) {
            margin.top = 0;
            margin.bottom = 0;
        }

        this.margin = margin;

        // Notify children of size and margin change
        this.plotParts.forEach((x) => {
            x.onSize(this.size, this.margin);
        });

        this.redraw();

        return true;
    }

    private measureViewPort(): void {
        this.viewPort = this.computeViewport();
        const bounds = this.getActualBounds();

        this.plotParts.forEach((x) => {
            x.onViewPortComputed(this.viewPort, bounds, new Pair<Unit>(Time.second, this.yUnit));
        });

        if (this.spectrumBacklog && this.spectrumBacklog.length > 0) {
            this.updateMinMax(bounds);
        }

        this.redraw();
    }

    public fixedExtremaChanged(): void {
        // Extrema changed, redraw
        this.redraw();
    }

    protected axisStartOrEndChanged(): void {
        // Check if it really changed
        if (
            Math.abs(this.viewPort.x - this.xAxisStart) > 1e-5 ||
            Math.abs(this.viewPort.y - this.yAxisStart) > 1e-5 ||
            Math.abs(this.viewPort.width - (this.xAxisEnd - this.xAxisStart)) > 1e-5 ||
            Math.abs(this.viewPort.height - (this.yAxisEnd - this.yAxisStart)) > 1e-5
        )
            this.requestViewPort();
    }

    private getActualBounds(): Rect {
        const x =
            this.xAxisBoundStart == undefined
                ? this.viewPortBounds.x
                : Math.max(this.xAxisBoundStart, this.viewPortBounds.x);
        const y =
            this.yAxisBoundStart == undefined
                ? this.viewPortBounds.y
                : Math.max(this.yAxisBoundStart, this.viewPortBounds.y);
        return new Rect(
            x,
            y,
            (this.xAxisBoundEnd == undefined
                ? this.viewPortBounds.x + this.viewPortBounds.width
                : Math.min(this.xAxisBoundEnd, this.viewPortBounds.x + this.viewPortBounds.width)) - x,
            (this.yAxisBoundEnd == undefined
                ? this.viewPortBounds.y + this.viewPortBounds.height
                : Math.min(this.yAxisBoundEnd, this.viewPortBounds.y + this.viewPortBounds.height)) - y
        );
    }

    private computeViewport(): Rect {
        let xStart = this.xAxisStart === undefined ? 0 : this.xAxisStart;
        let xEnd = this.xAxisEnd === undefined ? 1 : this.xAxisEnd;
        let yStart = this.yAxisStart === undefined ? 0 : this.yAxisStart;
        let yEnd = this.yAxisEnd === undefined ? 1 : this.yAxisEnd;

        const bounds = this.getActualBounds();

        if (xStart < bounds.x) xStart = bounds.x;

        if (xStart > bounds.x + bounds.width) xStart = bounds.x;

        if (xEnd > bounds.x + bounds.width) xEnd = bounds.x + bounds.width;

        if (xEnd < bounds.x) xEnd = bounds.x + bounds.width;

        if (yStart < bounds.y) yStart = bounds.y;

        if (yStart > bounds.y + bounds.height) yStart = bounds.y;

        if (yEnd > bounds.y + bounds.height) yEnd = bounds.y + bounds.height;

        if (yEnd < bounds.y) yEnd = bounds.y + bounds.height;

        return new Rect(xStart, yStart, xEnd - xStart, yEnd - yStart);
    }

    private updateMinMax(bounds: Rect): void {
        const offsetTime = this.getDrawTimeOffset();
        const thresholdTime = bounds.x + offsetTime;

        const index = this.findFirstVisibleIndex(thresholdTime);

        if (index === -1) {
            return;
        }

        // Make sure textures are bound
        this.gl.activeTexture(WebGLRenderingContext.TEXTURE0);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.dataTexture);

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);

        this.minMaxProgram.updateData(
            0,
            index,
            Math.min(this.textureIndex, SpectrogramBasePlot.MaximumTextureDimension) - index,
            this.spectrumBacklog.length === 0 ? 0 : this.spectrumBacklog[0].data.length,
            this.viewPort,
            bounds
        );
    }

    private colormapChanged(): void {
        if (!this.gl) return;

        // Update colormap texture
        this.updateColorMapTexture();

        // Update clear color
        if (this.colormap) {
            const clearColor = this.colormap.toColor(0);
            this.gl.clearColor(clearColor.r, clearColor.g, clearColor.b, 1.0);
        } else {
            this.gl.clearColor(0, 0, 0, 1);
        }

        this.redraw();
    }

    private fullscreenChanged = (ev: Event) => {
        this.isFullScreen = !!document.fullscreenElement;
    };

    //#endregion

    //#region User Input Methods

    private toggleFullScreen(): void {
        if (this.isFullScreen) {
            document.exitFullscreen();
        } else {
            this.controlContainer.requestFullscreen();
        }
    }

    //#endregion

    //#region Update Functions

    protected clearSpectra(): void {
        this.spectrumBacklog = [];
        this.vertexRanges = [];
        this.textureIndex = 0;
    }

    protected addSpectra(spectra: UnitSpectrum[]): void {
        this.updateSpectra(spectra);
        this.updateSpectrumTextureCoordinates();
    }

    private updateSpectra(spectra: UnitSpectrum[]): void {
        if (!spectra) return;

        const bounds = this.getActualBounds();
        let startIndex = this.textureIndex;
        for (let j = 0; j < spectra.length; j++, this.textureIndex++) {
            const spectrum = (<WeightedSpectrum>spectra[j]).weighting
                ? <WeightedSpectrum>spectra[j]
                : WeightedSpectrum.FromUnitSpectrum(new ZWeighting(), spectra[j]);

            // Apply weighting if the display unit is a weighted unit
            if ((<WeightedUnit>this.displayUnit).weighting)
                spectrum.weighting = (<WeightedUnit>this.displayUnit).weighting;

            if (this.textureIndex >= SpectrogramBasePlot.MaximumTextureDimension) {
                const offsetTime = this.getDrawTimeOffset();
                const thresholdTime = bounds.x + offsetTime;

                // We have exceeded the storage of our texture, removed old data
                const index = this.findFirstVisibleIndex(thresholdTime);

                // Draw the current texture to the framebuffer
                this.textureProgram.draw(this.gl, this.dataTexture, this.frameBuffer);

                // Copy datatexture 2 to 1 partially
                this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.dataTexture);
                this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, this.frameBuffer);
                this.gl.copyTexSubImage2D(
                    WebGLRenderingContext.TEXTURE_2D,
                    0,
                    0,
                    0,
                    index,
                    0,
                    SpectrogramBasePlot.MaximumTextureDimension - index,
                    SpectrogramBasePlot.MaximumTextureDimension
                );

                this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, null);
                this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, null);

                // Remove vertices
                this.vertexData.copyWithin(0, index * 4, this.vertexData.length);
                this.texelData.copyWithin(0, index * 4, this.texelData.length);
                this.spectrumBacklog.copyWithin(0, index, this.spectrumBacklog.length);

                // Update existing data
                this.textureIndex = startIndex = SpectrogramBasePlot.MaximumTextureDimension - index;
                this.textureCoordinateIndex = 0;

                for (let i = 0; i < this.textureIndex; i++) {
                    this.vertexData[i * 4] = this.vertexData[(i + index) * 4];
                    this.vertexData[i * 4 + 2] = this.vertexData[(i + index) * 4 + 2];
                    this.texelData[i * 4] -= index / (SpectrogramBasePlot.MaximumTextureDimension - 1);
                    this.texelData[i * 4 + 2] -= index / (SpectrogramBasePlot.MaximumTextureDimension - 1);
                }

                for (let i = this.textureIndex; i < SpectrogramBasePlot.MaximumTextureDimension; i++) {
                    this.spectrumBacklog[i] = undefined;
                }
            }

            // Compute bytes for the next part of spectrum
            let i = 0;
            let value = 0;
            for (; i < Math.min(spectrum.data.length, SpectrogramBasePlot.MaximumTextureDimension); i++) {
                value = spectrum.getYUnitValue(i).toUnit(this.displayUnit);
                value = Math.max(Math.min(value, this.max), this.min);
                value = Math.floor(((value - this.min) / (this.max - this.min)) * SpectrogramBasePlot.MaxTextureValue);

                this.spectrumTextureDataBufferUint32View[i * spectra.length + (this.textureIndex - startIndex)] = value;
            }

            // Repeat last value in the rest of the array
            for (; i < SpectrogramBasePlot.MaximumTextureDimension; i++) {
                this.spectrumTextureDataBufferUint32View[i * spectra.length + (this.textureIndex - startIndex)] = value;
            }

            // Set timestamp for this spectrum
            this.spectrumBacklog[this.textureIndex] = spectrum;
        }

        // Update texture data
        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.dataTexture);
        this.gl.texSubImage2D(
            WebGLRenderingContext.TEXTURE_2D,
            0,
            startIndex,
            0,
            this.textureIndex - startIndex,
            SpectrogramBasePlot.MaximumTextureDimension,
            this.gl.RGBA,
            this.gl.UNSIGNED_BYTE,
            this.spectrumTextureDataBufferUint8View
        );
        this.gl.generateMipmap(WebGLRenderingContext.TEXTURE_2D);

        this.updateMinMax(bounds);
    }

    private updateColorMapTexture(): void {
        if (!this.colormap || !this.colorMapTexture) return;

        // Create data for texture
        if (!this.colormapTextureDataBuffer)
            this.colormapTextureDataBuffer = new Uint8Array(SpectrogramBasePlot.ColorMapResolution * 4);

        const colorData = this.colormap.toColorRange(SpectrogramBasePlot.ColorMapResolution);
        for (let i = 0; i < SpectrogramBasePlot.ColorMapResolution; i++) {
            this.colormapTextureDataBuffer[i * 4] = Math.max(Math.min(colorData[i].r * 255, 255), 0);
            this.colormapTextureDataBuffer[i * 4 + 1] = Math.max(Math.min(colorData[i].g * 255, 255), 0);
            this.colormapTextureDataBuffer[i * 4 + 2] = Math.max(Math.min(colorData[i].b * 255, 255), 0);
            this.colormapTextureDataBuffer[i * 4 + 3] = Math.max(Math.min(colorData[i].a * 255, 255), 0);
        }

        this.gl.activeTexture(this.gl.TEXTURE1);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.colorMapTexture);
        this.gl.texImage2D(
            WebGLRenderingContext.TEXTURE_2D,
            0,
            this.gl.RGBA,
            SpectrogramBasePlot.ColorMapResolution,
            1,
            0,
            this.gl.RGBA,
            this.gl.UNSIGNED_BYTE,
            this.colormapTextureDataBuffer
        );

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

        this.gl.generateMipmap(WebGLRenderingContext.TEXTURE_2D);
    }

    private updateSpectrumTextureCoordinates(): void {
        // Update all new spectrum coordinates
        for (let idx = this.textureCoordinateIndex; idx < this.textureIndex; idx++) {
            const spectrum = this.spectrumBacklog[idx];

            const vx = (spectrum.properties.timeRange.from + spectrum.properties.timeRange.to) / 2;
            const vy0 = spectrum.data[0].x;
            const vy1 = spectrum.data[spectrum.data.length - 1].x;

            this.vertexData[idx * 4] = vx;
            this.vertexData[idx * 4 + 1] = vy0;
            this.vertexData[idx * 4 + 2] = vx;
            this.vertexData[idx * 4 + 3] = vy1;

            // Set next 2 coordinates
            const tx = idx / (SpectrogramBasePlot.MaximumTextureDimension - 1);
            const ty0 = 0;
            const ty1 = Math.min((spectrum.data.length - 1) / (SpectrogramBasePlot.MaximumTextureDimension - 1), 1);

            this.texelData[idx * 4] = tx;
            this.texelData[idx * 4 + 1] = ty0;
            this.texelData[idx * 4 + 2] = tx;
            this.texelData[idx * 4 + 3] = ty1;
        }

        // Update buffer data
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer);
        this.gl.bufferSubData(
            this.gl.ARRAY_BUFFER,
            this.textureCoordinateIndex * 4 * 4,
            this.vertexData.subarray(this.textureCoordinateIndex * 4, (this.textureIndex + 1) * 4)
        );

        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texelBuffer);
        this.gl.bufferSubData(
            this.gl.ARRAY_BUFFER,
            this.textureCoordinateIndex * 4 * 4,
            this.texelData.subarray(this.textureCoordinateIndex * 4, (this.textureIndex + 1) * 4)
        );

        this.textureCoordinateIndex = this.textureIndex;

        // Determine which parts of the data should be drawn
        this.vertexRanges = [{ start: 0, length: this.textureCoordinateIndex * 2 }];
    }

    //#endregion

    //#region Helpers

    private findFirstVisibleIndex(thresholdTime: number): number {
        return ArrayExtensions.fastIndexOf(this.spectrumBacklog, (spectrum, index, array) => {
            if (spectrum === undefined) return -1;

            if (index === array.length - 1) return 0;

            let time = (spectrum.properties.timeRange.from + spectrum.properties.timeRange.to) / 2;

            if (time < thresholdTime) return 1;

            if (index === 0) return 0;

            const spm1 = array[index - 1];
            time = (spm1.properties.timeRange.from + spm1.properties.timeRange.to) / 2;

            if (time < thresholdTime) return 0;

            return -1;
        });
    }

    //#endregion

    //#region Draw Functions

    protected redraw(updateExtrema = true): void {
        if (!this.gl) return;

        // Draw waterfall
        this.drawWaterfall(updateExtrema);
    }

    protected getDrawTimeOffset(): number {
        return 0;
    }

    private drawWaterfall(updateExtrema: boolean): void {
        // Update clear color
        if (this.colormap) {
            const clearColor = this.colormap.toColor(0);
            this.gl.clearColor(clearColor.r, clearColor.g, clearColor.b, 1.0);
        } else {
            this.gl.clearColor(0, 0, 0, 1);
        }

        // Clear buffer
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);

        if (!this.vertexRanges || !this.spectrumBacklog || !this.spectrumBacklog.length || !this.textureIndex) return;

        // Determine offset of the texture in which to determine min/max
        const bounds = this.getActualBounds();
        const offsetTime = this.getDrawTimeOffset();
        const thresholdTime = bounds.x + offsetTime;

        let windowExtrema: { min: number; max: number };
        // Use the ordered time array to find the index fast
        if (updateExtrema || !this.currentExtrema) {
            // Determine data min max
            this.currentExtrema = this.minMaxProgram.determineMinMax();
        }
        windowExtrema = this.currentExtrema;

        this.gl.activeTexture(WebGLRenderingContext.TEXTURE0);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.dataTexture);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);

        this.gl.activeTexture(WebGLRenderingContext.TEXTURE1);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.colorMapTexture);

        // Determine min/max values for draw time
        let unit: Unit = this.displayUnit;
        unit = unit ? unit : Generic.unitless;

        if (this.fixedMinimum) {
            let value = this.fixedMinimum.toUnit(unit);
            value = Math.max(Math.min(value, this.max), this.min);
            value = ((value - this.min) / (this.max - this.min)) * SpectrogramBasePlot.MaxTextureValue;

            windowExtrema.min = value;
        }

        if (this.fixedMaximum) {
            let value = this.fixedMaximum.toUnit(unit);
            value = Math.max(Math.min(value, this.max), this.min);
            value = ((value - this.min) / (this.max - this.min)) * SpectrogramBasePlot.MaxTextureValue;

            windowExtrema.max = value;
        }

        if (windowExtrema.max < windowExtrema.min) windowExtrema.max = windowExtrema.min;

        const newMin = (windowExtrema.min / SpectrogramBasePlot.MaxTextureValue) * (this.max - this.min) + this.min;
        const newMax = (windowExtrema.max / SpectrogramBasePlot.MaxTextureValue) * (this.max - this.min) + this.min;

        if (!this.dataMinimum || newMin !== this.dataMinimum.value || unit !== this.dataMinimum.unit)
            this.dataMinimum = new UnitValue(newMin, unit);

        if (!this.dataMaximum || newMax !== this.dataMaximum.value || unit !== this.dataMaximum.unit)
            this.dataMaximum = new UnitValue(newMax, unit);

        this.gl.activeTexture(WebGLRenderingContext.TEXTURE1);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.colorMapTexture);

        // Update webgl viewport
        this.gl.viewport(
            0,
            0,
            this.size.width - this.margin.left - this.margin.right,
            this.size.height - this.margin.top - this.margin.bottom
        );

        // Enable program
        this.spectrogramProgram.enable(this.gl, this.vertexBuffer, this.texelBuffer);

        // Set shader properties
        this.gl.uniform1i(this.spectrogramProgram.dataTextureLocation, 0);
        this.gl.uniform1i(this.spectrogramProgram.colormapTextureLocation, 1);
        this.gl.uniform1f(this.spectrogramProgram.maximumLocation, windowExtrema.max);
        this.gl.uniform1f(this.spectrogramProgram.minimumLocation, windowExtrema.min);
        this.gl.uniform1f(this.spectrogramProgram.widthLocation, SpectrogramBasePlot.MaximumTextureDimension);
        this.gl.uniform1f(this.spectrogramProgram.heightLocation, SpectrogramBasePlot.MaximumTextureDimension);

        this.gl.uniform1f(this.spectrogramProgram.xOriginLocation, this.viewPort.x + offsetTime);
        this.gl.uniform1f(this.spectrogramProgram.xWidthLocation, this.viewPort.width);

        this.gl.uniform1f(this.spectrogramProgram.yOriginLocation, this.viewPort.y);
        this.gl.uniform1f(this.spectrogramProgram.yHeightLocation, this.viewPort.height);

        // Draw
        for (let i = 0; i < this.vertexRanges.length; i++) {
            this.gl.drawArrays(this.gl.TRIANGLE_STRIP, this.vertexRanges[i].start, this.vertexRanges[i].length);
        }

        this.spectrogramProgram.disable(this.gl);
    }

    //#endregion
}
